import {
  get,
  isEmpty,
  isString,
  split,
  isArray,
  find,
  isEqual,
  join,
  map,
  pick,
  reduce,
  compact,
  uniqWith,
  includes,
  isNil,
  filter,
} from 'lodash';

import {
  IPdfDefinitions,
  IPdfColumnSelector,
  IPdfGrid,
  IPdfColumn,
  IPdfOptions,
  IPdfGridOptions,
} from '../pdf.type';

const mapGridColToColumns = (grid: IPdfGrid): IPdfColumn[] =>
  map(grid?.columns, (column) => pick(column, ['path', 'title', 'required']));

const getColumnsFromGrid = (
  definitions: IPdfDefinitions,
): IPdfColumnSelector['columns'] => {
  const columnsFromGridPath = get(
    definitions?.options,
    'columnSelector.columnsFromGridPath',
  );

  if (isEmpty(columnsFromGridPath)) {
    return [];
  }

  let girdPathArr = [
    columnsFromGridPath?.gridPath,
    columnsFromGridPath?.settingPath,
  ];

  if (isString(columnsFromGridPath)) {
    girdPathArr = split(columnsFromGridPath, '.');
  } else if (isArray(columnsFromGridPath)) {
    girdPathArr = columnsFromGridPath;
  }

  const [gridPath, ...settingPaths] = girdPathArr;

  const gridContent = get(definitions, ['content', gridPath]);

  let gridColumns: IPdfColumn[] = [];
  if (!isEmpty(compact(settingPaths))) {
    const gridSettingColumns: IPdfGrid = find(
      gridContent?.setting,
      (value, key) => {
        return isEqual(key, join(settingPaths, '.'));
      },
    );
    gridColumns = mapGridColToColumns(gridSettingColumns);
  } else {
    // use all gird setting grid columns(with uniq)
    gridColumns = reduce(
      gridContent?.setting,
      (preColumns, value: IPdfGrid) => {
        preColumns.push(...mapGridColToColumns(value));

        return preColumns;
      },
      [],
    );
  }

  return uniqWith(
    gridColumns,
    (col, anotherCol) =>
      isEqual(col?.path, anotherCol?.path) ||
      isEqual(col?.title, anotherCol?.title),
  );
};

const isSelectedItem = (
  selectedColumnPaths: string[],
  selectedColumnTitles: string[],
  col: IPdfColumn,
): boolean => {
  return (
    includes(selectedColumnPaths, col.path) ||
    includes(selectedColumnTitles, col.title)
  );
};

const getSelectedColumns = (
  girdColumns: IPdfColumn[],
  options: IPdfOptions,
): IPdfColumn[] => {
  const [selectedColumnPaths, selectedColumnTitles] = reduce(
    options?.selectedColumns,
    ([paths, titles], value) => {
      if (!isNil(value.path)) {
        paths.push(value?.path);
      }

      if (!isNil(value.title)) {
        titles.push(value?.title);
      }

      return [paths, titles];
    },
    [[], []],
  );

  // will get all columns in the selectedColumnPaths or column is required
  const selectedColumns = isNil(options?.selectedColumns)
    ? girdColumns
    : filter(girdColumns, (col) =>
        get(options.columnSelector, 'requiredNotDisabled')
          ? includes(selectedColumnPaths, col.path) ||
            includes(selectedColumnTitles, col.title)
          : col?.required ||
            includes(selectedColumnPaths, col.path) ||
            includes(selectedColumnTitles, col.title),
      );

  return selectedColumns;
};

const isEffectThisGrid = (
  gridOptions: IPdfGridOptions,
  pdfOptions: IPdfOptions,
): boolean => {
  if (isNil(pdfOptions?.columnSelector?.effectCondition)) {
    return true;
  }

  const { fieldPath, contentKey } = gridOptions;

  let isEffectGrid = true;
  const type = get(pdfOptions.columnSelector.effectCondition, 'type');
  const grids = get(pdfOptions.columnSelector.effectCondition, 'paths');

  switch (type) {
    case 'includes':
      isEffectGrid = includes(grids, fieldPath) || includes(grids, contentKey);
      break;
    case 'excludes':
      isEffectGrid =
        !includes(grids, fieldPath) && !includes(grids, contentKey);
      break;
    case 'all':
    default:
      break;
  }

  return isEffectGrid;
};

export {
  mapGridColToColumns,
  getColumnsFromGrid,
  getSelectedColumns,
  isEffectThisGrid,
  isSelectedItem,
};
